
import { EventBus } from '@/shared/app/connector/EventBus';
import AppConstants from '@/shared/app/model/AppConstants';
import { StyleValue } from '@/shared/app/model/StyleValue';
import Template from '@/shared/app/model/templating/Template';
import Dataset from '@/shared/data/model/Dataset';
import BaseDishGroup from '@/shared/dish/ui/DishGroup.vue';
import _ from 'lodash';
import Component from 'vue-class-component';
import { TemplateMaker } from '../../../makers/types/template-maker';

@Component({})
export default class DishesGroup extends BaseDishGroup {
  activePage = 0;

  mounted() {
    EventBus.$on(AppConstants.DISHES_ROTATION_EVENT_NAME, this.nextPage);
  }

  beforeDestroy() {
    EventBus.$off(AppConstants.DISHES_ROTATION_EVENT_NAME);
  }

  // Processing
  nextPage() {
    let nextIndex = this.activePage + 1;
    const numberOfPages = this.indexesByPages.length;
    // Correction si boucle terminée
    if (nextIndex >= numberOfPages) nextIndex = 0;
    this.activePage = nextIndex;
  }

  // Getters styles
  get rowsSeparatorStyle(): StyleValue {
    return {
      height: this.config.spaceBetween?.toString() || `${this.sizeFactor * 2}rem`,
    };
  }

  get dishesSeparatorStyle(): StyleValue {
    return {
      width:
        this.config.spaceBetweenDishes?.toString() ||
        this.config.spaceBetween?.toString() ||
        `${this.sizeFactor * 2}rem`,
      backgroundColor: this.config.separatorColor?.toString() || 'transparent',
    };
  }

  get noDishesStyle(): StyleValue {
    return {
      fontSize: `${this.sizeFactor * 2.6}rem`,
    };
  }

  // Getters layout
  get indexesByPages() {
    // const activeDishesCount = this.activeDishesIndexes.length;
    // Découpage 1/2: les PAGES
    const indexesForPages = _.chunk(this.activeDishesIndexes, this.numberOfDishesPerPage);
    // Découpage 2/2: les LIGNES
    const pages: number[][][] = [];
    indexesForPages.forEach((pageIndexes) => {
      let pageRows = _.chunk(pageIndexes, this.numberOfDishesPerRow);
      // Renflouer la ligne si incomplète par rapport aux settings
      // Ajout de plat(s) fake si le compte n'y est pas
      if (pageRows[pageRows.length - 1].length < this.minNumberOfDishesPerRow) {
        const lastRow = pageRows[pageRows.length - 1];
        pageRows[pageRows.length - 1] = lastRow.concat(
          Array(this.minNumberOfDishesPerRow - lastRow.length).fill(-1),
        );
      }
      // Ajout d'une ligne fake si le compte n'y est pas
      if (pageRows.length < this.minNumberOfRowsPerPage) {
        pageRows = pageRows.concat(
          Array(this.minNumberOfRowsPerPage - pageRows.length).fill(
            Array(this.minNumberOfDishesPerRow).fill(-1),
          ),
        );
      }
      pages.push(pageRows);
    });
    return pages;
  }

  dishTemplate(dishIndex: number): Template {
    return {
      component: this.component,
      config: {
        sizeFactor: this.computedSizeFactor,
        ...this.dataset('dishConfig'),
        index: dishIndex,
      },
      datasets: {
        dishes: this.dishes,
        origins: this.dataset('dishOrigins'),
      },
    };
  }

  get style(): StyleValue {
    const style: StyleValue = {
      padding: this.config.padding?.toString() || 'auto',
      borderRadius: this.config.borderRadius?.toString() || '0',
    };
    if (this.config.backgroundColor) style.backgroundColor = this.config.backgroundColor.toString();
    return style;
  }

  get indicatorStyle(): StyleValue {
    const paddingTopBottom = this.config.indicatorPadding?.toString() || '0';
    return {
      paddingTop: paddingTopBottom,
      paddingBottom: paddingTopBottom,
      paddingLeft: `2.3%`,
      paddingRight: `2.3%`,
    };
  }

  get indicatorBarStyle(): StyleValue {
    return {
      height: this.config.indicatorThickness?.toString() || '.4rem',
    };
  }

  get indicatorValueStyle(): StyleValue {
    const onePageWidth = 100 / this.indexesByPages.length;
    return {
      width: `${onePageWidth}%`,
      left: `${this.activePage * onePageWidth}%`,
    };
  }

  // Getters configuration
  get minSizeFactor() {
    return Number(this.config.minSizeFactor) || 2;
  }

  get maxSizeFactor() {
    return Number(this.config.maxSizeFactor) || 2;
  }

  get computedSizeFactor() {
    const sizeFactorFromDishConfig = Number(this.dataset('dishConfig')?.sizeFactor);
    if (sizeFactorFromDishConfig) return sizeFactorFromDishConfig;

    const numberOfRowsOnFirstPage = Math.max(
      this.minNumberOfRowsPerPage,
      this.indexesByPages[0].length,
    );
    const numberOfRowsIfFirstPageIsFull = Math.max(
      this.minNumberOfRowsPerPage,
      Math.ceil(this.numberOfDishesPerPage / this.numberOfDishesPerRow),
    );
    const sizeFactorReductionRatio = numberOfRowsOnFirstPage / numberOfRowsIfFirstPageIsFull;
    const sizeFactor =
      this.maxSizeFactor - (this.maxSizeFactor - this.minSizeFactor) * sizeFactorReductionRatio;
    return sizeFactor;
  }

  get numberOfDishesPerPage() {
    return Number(this.config.numberOfDishesPerPage) || 4;
  }

  get numberOfDishesPerRow() {
    return Number(this.config.numberOfDishesPerRow) || 2;
  }

  get minNumberOfDishesPerRow() {
    return Number(this.config.minNumberOfDishesPerRow) || 1;
  }

  get minNumberOfRowsPerPage() {
    return Number(this.config.minNumberOfRowsPerPage) || 1;
  }

  get pagesTransition() {
    return this.config.pagesTransition?.toString() || 'slideup';
  }

  get showIndicator() {
    return Boolean(this.config.showIndicator);
  }

  get noDishesMessage(): string | undefined {
    return this.dishes.no_dishes_message?.toString() || this.config.noDishesMessage?.toString();
  }
}

// à compléter au fur et à mesure
type DishesGroupMakerArgs = {
  component?: string;
  dataset?: string;
  componentConfig?: Dataset;
  layout?: {
    numberOfDishesPerPage?: number;
    numberOfDishesPerRow?: number;
    minNumberOfDishesPerRow?: number;
    minNumberOfRowsPerPage?: number;
    maxDishes?: number;
    pagesTransition?: string;
    spaceBetween?: string;
    spaceBetweenDishes?: string;
    separatorColor?: string;
  };
  indicator?: {
    show: boolean;
    padding?: string;
  };
  noDishesMessage?: string;
};

export const makeUniDishesGroup: TemplateMaker<DishesGroupMakerArgs> = (args) => {
  return {
    component: 'DishesGroup',
    config: {
      ...(args.component && { component: args.component }),

      ...(args.layout?.numberOfDishesPerPage && {
        numberOfDishesPerPage: args.layout.numberOfDishesPerPage,
      }),
      ...(args.layout?.numberOfDishesPerRow && {
        numberOfDishesPerRow: args.layout.numberOfDishesPerRow,
      }),
      ...(args.layout?.minNumberOfDishesPerRow && {
        minNumberOfDishesPerRow: args.layout.minNumberOfDishesPerRow,
      }),
      ...(args.layout?.minNumberOfRowsPerPage && {
        minNumberOfRowsPerPage: args.layout.minNumberOfRowsPerPage,
      }),
      ...(args.layout?.maxDishes && { maxDishes: args.layout.maxDishes }),
      ...(args.layout?.pagesTransition && { pagesTransition: args.layout.pagesTransition }),
      ...(args.layout?.spaceBetween && { spaceBetween: args.layout.spaceBetween }),
      ...(args.layout?.spaceBetweenDishes && {
        spaceBetweenDishes: args.layout.spaceBetweenDishes,
      }),
      ...(args.layout?.separatorColor && { separatorColor: args.layout.separatorColor }),

      ...(args.indicator?.show && { showIndicator: args.indicator.show }),
      ...(args.indicator?.padding && { indicatorPadding: args.indicator.padding }),

      ...(args.noDishesMessage && { noDishesMessage: args.noDishesMessage }),
    },
    datasets: {
      ...(args.dataset && { dishes: args.dataset }),
      ...(args.componentConfig && { dishConfig: args.componentConfig }),
    },
    slots: {},
  };
};
